// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI — reading a period's frames into a climatology as playback passes
 * them (`climatology.ts`; research tier, statistics S2).
 *
 * Each frame of the period is read once, whichever way it reaches the globe
 * (playing, seeking, stepping), and only from the dataset and grid the
 * reading started on; a frame shown twice (playback loops) is not counted
 * twice. Frames the publisher marks as filled, interpolated, estimated or
 * missing (the .dsa availability) are not read and count as missing.
 *
 * The climatology is made when every readable frame has been read, or
 * earlier at the reader's request once every group has 80 % of its frames
 * (WMO-No. 1203's completeness guideline), with the count stated. Nothing
 * is compared against a period still being read, so the baseline never
 * changes while it is used.
 *
 * Pure: no DOM.
 */

import {
  MIN_COVERAGE,
  accumulateFrame,
  calendarFor,
  checkPeriod,
  codeScale,
  createAccumulator,
  expectedGroups,
  finishClimatology,
  periodFrames,
  type ClimateAxis,
  type ClimateCalendar,
  type ClimatePeriod,
  type Climatology,
  type ClimatologyAccumulator,
  type CodeScale,
  type PeriodCheck,
  type PeriodFrame,
} from './climatology'

export interface PeriodRunSpec {
  readonly datasetId: string
  readonly datasetTitle: string | null
  readonly units?: string
  /** Where the frame sits on the Earth (`baseline.gridKey`): frames from another grid are not read. */
  readonly gridKey: string
  readonly width: number
  readonly height: number
  /** Each code's value, NaN for no data (`changeMap.valueTable`). */
  readonly values: ArrayLike<number>
  readonly axis: ClimateAxis
  /** Null for a window (all of it). */
  readonly period: ClimatePeriod | null
  /** Whether the publisher measured frame i (the .dsa availability); all are, without one. */
  readonly usable?: (frame: number) => boolean
  /** The memory cap for the sums (tests use a small one). */
  readonly budget?: number
}

export class PeriodRun {
  readonly spec: PeriodRunSpec
  readonly calendar: ClimateCalendar
  readonly frames: readonly PeriodFrame[]
  readonly check: PeriodCheck
  /** The data's codes; null for a table that is not one monotonic run (nothing can be held). */
  readonly scale: CodeScale | null
  private readonly byFrame = new Map<number, PeriodFrame>()
  private readonly done = new Set<number>()
  private readonly readPerGroup: number[]
  private acc: ClimatologyAccumulator | null
  private result: Climatology | null = null

  constructor(spec: PeriodRunSpec) {
    this.spec = spec
    this.calendar = calendarFor(spec.axis)
    this.frames = periodFrames(spec.axis, this.calendar, spec.period, spec.usable)
    this.check = checkPeriod(this.frames, this.calendar)
    this.scale = codeScale(spec.values)
    for (const f of this.frames) if (f.usable) this.byFrame.set(f.frame, f)
    this.readPerGroup = new Array<number>(this.calendar.groups).fill(0)
    const maxPerGroup = Math.max(1, ...this.check.framesPerGroup)
    this.acc = this.check.refusal || !this.scale ? null : createAccumulator(spec.width, spec.height, this.calendar.groups, maxPerGroup, spec.budget)
  }

  /** Readable frames in the period. */
  get total(): number {
    return this.check.frames
  }

  /** Frames read so far. */
  get read(): number {
    return this.done.size
  }

  /** Every texel (1), or every 2nd, 3rd … each way to stay within the memory cap. */
  get factor(): number {
    return this.acc?.factor ?? this.result?.factor ?? 1
  }

  /** Whether a frame of the axis belongs to the period, can be read, and is still to be read. */
  wants(frame: number): boolean {
    return this.acc !== null && this.byFrame.has(frame) && !this.done.has(frame)
  }

  /** The period's readable frames not read yet, in order. */
  missing(): number[] {
    return this.frames.filter((f) => f.usable && !this.done.has(f.frame)).map((f) => f.frame)
  }

  /**
   * Read one frame: its codes, the dataset and grid it came from. False
   * (and nothing counted) for a frame outside the period, unreadable,
   * already read, or from another dataset or grid.
   */
  accept(frame: number, from: { datasetId: string | null; gridKey: string; codes: Uint8Array; width: number; height: number }): boolean {
    if (!this.wants(frame) || from.datasetId !== this.spec.datasetId || from.gridKey !== this.spec.gridKey) return false
    const pf = this.byFrame.get(frame)!
    if (!accumulateFrame(this.acc!, from.codes, from.width, from.height, this.spec.values, pf)) return false
    this.done.add(frame)
    this.readPerGroup[pf.group]++
    return true
  }

  /** Every readable frame of the period read. */
  complete(): boolean {
    return this.acc !== null && this.done.size === this.check.frames
  }

  /** Every group the axis visits has 80 % of its frames read (skipped ones counting as missing): enough to finish early. */
  enough(): boolean {
    if (!this.acc) return false
    return expectedGroups(this.calendar).every((g) => this.readPerGroup[g] >= Math.ceil(MIN_COVERAGE * this.check.expectedPerGroup[g] - 1e-9) && this.readPerGroup[g] >= 2)
  }

  /**
   * The climatology, once complete (or, with `early`, once {@link enough});
   * null before. The sums become 16-bit means and σ, and are freed; σ is
   * estimated unless the period is a window.
   */
  climatology(early = false): Climatology | null {
    if (this.result) return this.result
    if (!this.acc || !this.scale || !(this.complete() || (early && this.enough()))) return null
    this.result = finishClimatology(this.acc, {
      sigma: this.calendar.grouping !== 'window',
      scale: this.scale,
      calendar: this.calendar,
      expectedPerGroup: this.check.expectedPerGroup,
    })
    this.acc = null
    return this.result
  }
}

/**
 * AYNI: "Read the missing frames", for any reader of frames (a period, the
 * area over a threshold): show each frame in turn (`showFrame` pauses
 * playback and resolves once the frame is on the globe) and read it, until
 * `stopped()` says otherwise. Resolves with how many frames were read.
 */
export async function stepFrames(
  frames: readonly number[],
  showFrame: (frame: number) => Promise<boolean>,
  read: (frame: number) => void,
  stopped: () => boolean,
): Promise<number> {
  let done = 0
  for (const frame of frames) {
    if (stopped()) break
    const shown = await showFrame(frame)
    if (stopped()) break
    if (!shown) continue
    read(frame)
    done++
  }
  return done
}
